import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import SaveIcon from "@material-ui/icons/Save";
import { toast } from "sonner";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import HolidaysAccordion from "../../components/Feriados/HolidaysAccordion";
import useHolidays from "../../hooks/useHolidays";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  messageSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  messageField: {
    backgroundColor: theme.palette.background.paper,
  },
  saveRow: {
    display: "flex",
    justifyContent: "flex-end",
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 300,
  },
  helpText: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
}));

const Feriados = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);

  const {
    holidays,
    holidayMessage,
    loading,
    updateHoliday,
    saveHolidayMessage,
    setHolidayMessage,
    createCustomHoliday,
  } = useHolidays();

  useEffect(() => {
    if (user.profile !== "admin") {
      history.push("/tickets");
    }
  }, [user.profile, history]);

  if (user.profile !== "admin") {
    return null;
  }

  const handleSaveMessage = async () => {
    setSaving(true);
    await saveHolidayMessage(holidayMessage);
    setSaving(false);
    toast.success(i18n.t("feriados.messageSaved"));
  };

  const handleToggleActive = (holidayId, active) => {
    updateHoliday(holidayId, { active });
  };

  const handleToggleCloseTicket = (holidayId, closeTicket) => {
    updateHoliday(holidayId, { closeTicket });
  };

  if (loading) {
    return (
      <MainContainer>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("feriados.title")}</Title>
      </MainHeader>

      <Paper className={classes.root} variant="outlined">
        <Box className={classes.messageSection}>
          <Typography className={classes.sectionTitle}>
            {i18n.t("feriados.standardMessage")}
          </Typography>
          <Typography className={classes.helpText}>
            {i18n.t("feriados.standardMessageHelp")}
          </Typography>
          <TextField
            className={classes.messageField}
            multiline
            minRows={3}
            maxRows={6}
            variant="outlined"
            fullWidth
            placeholder={i18n.t("feriados.standardMessagePlaceholder")}
            value={holidayMessage}
            onChange={(e) => setHolidayMessage(e.target.value)}
          />
          <Box className={classes.saveRow}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              onClick={handleSaveMessage}
            >
              {i18n.t("feriados.saveMessage")}
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography className={classes.sectionTitle}>
            {i18n.t("feriados.holidaysList")}
          </Typography>
          <Typography className={classes.helpText}>
            {i18n.t("feriados.holidaysListHelp")}
          </Typography>
          <Box mt={2}>
            <HolidaysAccordion
              holidays={holidays}
              onToggleActive={handleToggleActive}
              onToggleCloseTicket={handleToggleCloseTicket}
              onCreateCustom={createCustomHoliday}
            />
          </Box>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default Feriados;
